
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Text } from '../../../../../../react-konva';


import nodesCommonPropTypes from '../nodesCommonPropTypes';
import {
  resolveCanvasFontStyle,
  resolveFontFamilyForCanvas,
} from '../../../../utils/normalizeAnnotationFontStylesForLoad';

const TextNode = ({
  id,
  name,
  text,
  fontFamily = 'Arial',
  fontSize = 14,
  fontStyle,
  fill = '#000',
  x,
  y,
  width = 0,
  height = 0,
  scaleX,
  scaleY,
  rotation,
  annotationEvents,
  stroke,
  strokeWidth,
  shadowOffsetX,
  shadowOffsetY,
  shadowBlur,
  shadowColor,
  shadowOpacity,
  opacity,
  letterSpacing,
  lineHeight,
  align = 'left',
  ...otherProps
}) => {
  const textRef = useRef(null);

  useEffect(() => {
    const node = textRef.current;
    if (!node || typeof document === 'undefined' || !document.fonts?.load) {
      return;
    }

    const canvasFamily = resolveFontFamilyForCanvas(fontFamily);
    const resolvedStyle = resolveCanvasFontStyle(fontFamily, fontStyle);
    const styleTokens = String(resolvedStyle)
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const cssStyle = styleTokens.includes('italic') ? 'italic' : 'normal';
    const cssWeight = styleTokens.includes('bold') ? '700' : '400';
    const quotedFamily = `"${canvasFamily}"`;

    Promise.all([
      document.fonts.load(`${cssStyle} ${cssWeight} ${fontSize}px ${quotedFamily}`),
      document.fonts.ready,
    ])
      .catch(() => undefined)
      .then(() => {
        node._setTextData?.();
        node.clearCache?.();
        node.getLayer?.()?.batchDraw?.();
      });
  }, [fontFamily, fontSize, fontStyle, text, width, height]);

  const canvasFontFamily = resolveFontFamilyForCanvas(fontFamily);

  return (
    <Text
      ref={textRef}
      id={id}
      name={name}
      rotation={rotation}
      scaleX={scaleX}
      scaleY={scaleY}
      stroke={stroke}
      strokeWidth={strokeWidth}
      shadowOffsetX={shadowOffsetX}
      shadowOffsetY={shadowOffsetY}
      shadowBlur={shadowBlur}
      shadowColor={shadowColor}
      shadowOpacity={shadowOpacity}
      opacity={opacity}
      fill={fill}
      text={text}
      fontFamily={canvasFontFamily}
      fontStyle={resolveCanvasFontStyle(fontFamily, fontStyle)}
      fontSize={fontSize}
      letterSpacing={letterSpacing}
      lineHeight={lineHeight}
      align={align}
      x={x}
      y={y}
      width={width}
      height={height}
      {...annotationEvents}
      {...otherProps}
    />
  );
};


TextNode.propTypes = {
  ...nodesCommonPropTypes.definitions,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  annotationEvents: PropTypes.instanceOf(Object).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  text: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  fill: PropTypes.string,
  letterSpacing: PropTypes.number,
  lineHeight: PropTypes.number,
  align: PropTypes.string,
};

export default TextNode;
